import type { UUID } from '@aline/common';
import { EditingMode, PermissionRole } from '@aline/common';
import type { AvatarInfo } from 'src/components/AlineAvatar';
import {
  LAUNCHER_PLACEHOLDER_TEXT,
  WORKFLOW_LAUNCHER_LABEL,
} from 'src/components/workflow/workflow-helpers';
import type { Apollo } from 'src/gql/generated';
import { getEntityDisplayDetails } from 'src/helpers/accounts';
import { appColors, roleAvatarColor } from 'src/theme';
import type { AccountSubset } from 'src/types';

export function userComparator(
  userA: Pick<Apollo.User, 'firstName' | 'email'>,
  userB: Pick<Apollo.User, 'firstName' | 'email'>
): number {
  const compareStringA = userA.firstName ?? userA.email;
  const compareStringB = userB.firstName ?? userB.email;

  return compareStringA.localeCompare(compareStringB);
}

export function compareByEmail(
  userA: Pick<Apollo.User, 'email' | '__typename'>,
  userB: Pick<Apollo.User, 'email' | '__typename'>
): number {
  return userA.email.localeCompare(userB.email);
}

export function userWithPlural(userIds: UUID[]): string {
  return userIds.length <= 1 ? 'user' : 'users';
}

export function mostPermissiveEditingMode(userRole: PermissionRole): EditingMode {
  switch (userRole) {
    case PermissionRole.Owner:
    case PermissionRole.Editor:
      return EditingMode.EDITING;
    case PermissionRole.Suggester:
      return EditingMode.SUGGESTING;
    case PermissionRole.Commenter:
      return EditingMode.COMMENTING;
    case PermissionRole.Viewer:
      return EditingMode.VIEWING;
  }
}

export const permissionsAscOrder: PermissionRole[] = [
  PermissionRole.Viewer,
  PermissionRole.Commenter,
  PermissionRole.Suggester,
  PermissionRole.Editor,
  PermissionRole.Owner,
];

interface DisplayDetails {
  primaryText: string;
  avatarColor?: string;
  secondaryText?: string;
}

export function getRoleDisplayDetails(
  role: Pick<Apollo.UserDefinedRole, 'roleName'>
): DisplayDetails {
  return { avatarColor: roleAvatarColor, primaryText: role.roleName };
}

export function getAssigneeDisplayDetails(
  assignee?: AccountSubset | null,
  assigneeRole?: Partial<Pick<Apollo.UserDefinedRole, 'roleName'>> | null
): ReturnType<typeof getEntityDisplayDetails> {
  const assigneeDetails = assignee ? getEntityDisplayDetails(assignee) : null;
  const assigneeRoleDetails = assigneeRole?.roleName
    ? { avatarColor: appColors.iconsType, primary: assigneeRole.roleName }
    : null;

  return assigneeDetails || assigneeRoleDetails || { primary: 'Unassigned' };
}

interface PermissionWithAccountAndRole extends Pick<Apollo.WorkflowPermission, 'isLauncher'> {
  account: AccountSubset | null;
  role: Partial<Pick<Apollo.UserDefinedRole, '__typename' | 'id' | 'roleName'>> | null;
}

/**
 * Who a permission stands for, as a name and avatar color. A template's launcher placeholder has
 * no user or role, so it goes by its flag rather than falling through to "Unassigned".
 */
export function getPermissionAssigneeDisplayDetails(
  permission?: PermissionWithAccountAndRole | null
): ReturnType<typeof getEntityDisplayDetails> {
  if (permission?.isLauncher)
    return {
      avatarColor: roleAvatarColor,
      primary: WORKFLOW_LAUNCHER_LABEL,
      secondary: LAUNCHER_PLACEHOLDER_TEXT,
    };
  return getAssigneeDisplayDetails(permission?.account, permission?.role);
}

/** What the avatar for a permission shows: the account, else the role, else the launcher's initial. */
export function getPermissionAvatarInfo(
  permission?: PermissionWithAccountAndRole | null
): AvatarInfo {
  if (permission?.isLauncher) return WORKFLOW_LAUNCHER_LABEL.charAt(0);
  return permission?.account ?? permission?.role ?? null;
}

interface RecipientAssignment {
  assignee: AccountSubset | null;
  assigneeRole: Partial<Pick<Apollo.UserDefinedRole, '__typename' | 'id' | 'roleName'>> | null;
  permission: Pick<Apollo.WorkflowPermission, 'isLauncher'>;
}

/**
 * Who a recipient (a signer, a cc, a field's action) stands for. On a template, the launcher's
 * recipient has no assignee or role until launch, so it is named by its permission's flag.
 */
export function getRecipientAssigneeDisplayDetails(
  recipient?: RecipientAssignment | null
): ReturnType<typeof getEntityDisplayDetails> {
  return getPermissionAssigneeDisplayDetails(toRecipientPermission(recipient));
}

export function getRecipientAvatarInfo(recipient?: RecipientAssignment | null): AvatarInfo {
  return getPermissionAvatarInfo(toRecipientPermission(recipient));
}

export function getPermissionUser(permission?: PermissionWithAccountAndRole | null) {
  const account = permission?.account;
  if (account?.__typename === 'User') return account;
  return null;
}

function toRecipientPermission(
  recipient?: RecipientAssignment | null
): PermissionWithAccountAndRole | null {
  if (!recipient) return null;
  return {
    account: recipient.assignee,
    isLauncher: recipient.permission.isLauncher,
    role: recipient.assigneeRole,
  };
}
